/**
 * MONSTER TOWN — CREATURE PICTURES. FISHING/BUGS CREW (src/catching/**).
 *
 * LAW #4, AND THE WHOLE POINT OF THE BOOK. A collection book that a pre-reader
 * cannot read is a book of pictures, so every fish and every bug has to BE a
 * picture — not a label, not a blob, not a generic "creature" glyph repeated
 * eighteen times. A kid has to be able to look at the page and say "I've got
 * the blue butterfly, I haven't got the beetle" without a grown-up in the room.
 *
 * WHY CANVAS AND NOT AN ICON
 * `src/ui/icons.ts` is the HUD crew's file and this crew does not edit it.
 * `CollectionEntry.draw?: (c, size)` exists in the frozen contract for exactly
 * this: 2D art for the big book page, authored by whoever owns the creature.
 * These painters are also what the world's 3D art tints itself from, so a bug
 * in the grass and its page in the book are the same animal (`CREATURE_LOOK`).
 *
 * The primitives live in `paint.ts`; the book's own furniture (the net, the
 * page tabs, the sky pips, the place pictures) lives in `bookArt.ts`.
 */

import { css, type PaletteName } from '../core/palette'
import { dot, ellipse, eye, fillStroke, INK, inBox, line, type Painter } from './paint'

// ---------------------------------------------------------------------------
// bugs
// ---------------------------------------------------------------------------

/** Wings, body, antennae. `a` is the outer wing, `b` the inner flash. */
export function butterfly(a: PaletteName, b: PaletteName): Painter {
  return (c, size) =>
    inBox(c, size, () => {
      // upper wings
      ellipse(c, 30, 38, 22, 24, -0.35)
      fillStroke(c, a)
      ellipse(c, 70, 38, 22, 24, 0.35)
      fillStroke(c, a)
      // lower wings
      ellipse(c, 34, 66, 16, 17, -0.2)
      fillStroke(c, b)
      ellipse(c, 66, 66, 16, 17, 0.2)
      fillStroke(c, b)
      dot(c, 27, 36, 5, b)
      dot(c, 73, 36, 5, b)
      // body
      ellipse(c, 50, 52, 7, 26)
      fillStroke(c, 'ink', 4)
      line(c, [
        [46, 26],
        [38, 12],
      ])
      line(c, [
        [54, 26],
        [62, 12],
      ])
      dot(c, 37, 11, 4, a)
      dot(c, 63, 11, 4, a)
      eye(c, 46, 33, 5)
      eye(c, 55, 33, 5)
    })
}

export const ladybug: Painter = (c, size) =>
  inBox(c, size, () => {
    ellipse(c, 50, 56, 34, 31)
    fillStroke(c, 'flowerRed')
    line(
      c,
      [
        [50, 26],
        [50, 86],
      ],
      5,
    )
    dot(c, 34, 48, 6, 'ink')
    dot(c, 66, 48, 6, 'ink')
    dot(c, 36, 68, 5, 'ink')
    dot(c, 64, 68, 5, 'ink')
    ellipse(c, 50, 28, 20, 15)
    fillStroke(c, 'ink', 4)
    eye(c, 42, 26, 5)
    eye(c, 58, 26, 5)
    line(c, [
      [40, 16],
      [32, 6],
    ])
    line(c, [
      [60, 16],
      [68, 6],
    ])
  })

export const bee: Painter = (c, size) =>
  inBox(c, size, () => {
    ellipse(c, 34, 34, 20, 15, -0.5)
    fillStroke(c, 'glass', 4)
    ellipse(c, 66, 34, 20, 15, 0.5)
    fillStroke(c, 'glass', 4)
    ellipse(c, 50, 60, 30, 24)
    fillStroke(c, 'flowerYellow')
    c.save()
    c.beginPath()
    c.ellipse(50, 60, 30, 24, 0, 0, Math.PI * 2)
    c.clip()
    c.fillStyle = INK
    c.fillRect(34, 34, 9, 54)
    c.fillRect(56, 34, 9, 54)
    c.restore()
    ellipse(c, 50, 60, 30, 24)
    c.lineWidth = 6
    c.stroke()
    eye(c, 40, 52, 6)
    eye(c, 60, 52, 6)
    line(c, [
      [42, 40],
      [36, 26],
    ])
    line(c, [
      [58, 40],
      [64, 26],
    ])
  })

export const grasshopper: Painter = (c, size) =>
  inBox(c, size, () => {
    // the big back leg — the thing that makes it a grasshopper at a glance
    line(
      c,
      [
        [56, 60],
        [76, 34],
        [70, 74],
        [86, 86],
      ],
      8,
    )
    ellipse(c, 46, 58, 32, 18, -0.15)
    fillStroke(c, 'monsterLime')
    ellipse(c, 22, 48, 15, 13)
    fillStroke(c, 'grassDark')
    eye(c, 18, 45, 6)
    line(c, [
      [16, 36],
      [6, 20],
    ])
    line(c, [
      [24, 34],
      [20, 16],
    ])
    line(
      c,
      [
        [40, 70],
        [34, 86],
      ],
      6,
    )
    line(
      c,
      [
        [54, 70],
        [52, 88],
      ],
      6,
    )
  })

export const firefly: Painter = (c, size) =>
  inBox(c, size, () => {
    // the glow first, so the lantern reads even at 40 px
    ellipse(c, 68, 66, 26, 24)
    c.fillStyle = css('flowerYellow')
    c.globalAlpha = 0.45
    c.fill()
    c.globalAlpha = 1
    ellipse(c, 34, 32, 20, 13, -0.55)
    fillStroke(c, 'glass', 4)
    ellipse(c, 62, 30, 20, 13, 0.55)
    fillStroke(c, 'glass', 4)
    ellipse(c, 48, 48, 24, 17, 0.5)
    fillStroke(c, 'inkSoft')
    ellipse(c, 68, 66, 15, 14)
    fillStroke(c, 'uiCoin')
    dot(c, 68, 66, 7, 'flowerWhite')
    ellipse(c, 30, 34, 13, 12)
    fillStroke(c, 'ink', 4)
    eye(c, 27, 32, 5)
    line(c, [
      [26, 24],
      [16, 12],
    ])
    line(c, [
      [34, 24],
      [32, 10],
    ])
  })

export const moth: Painter = (c, size) =>
  inBox(c, size, () => {
    ellipse(c, 28, 48, 25, 27, -0.25)
    fillStroke(c, 'wallLilac')
    ellipse(c, 72, 48, 25, 27, 0.25)
    fillStroke(c, 'wallLilac')
    dot(c, 24, 44, 8, 'monsterLilac')
    dot(c, 76, 44, 8, 'monsterLilac')
    dot(c, 24, 44, 4, 'ink')
    dot(c, 76, 44, 4, 'ink')
    ellipse(c, 50, 54, 10, 26)
    fillStroke(c, 'woodDark')
    ellipse(c, 50, 30, 12, 11)
    fillStroke(c, 'wood')
    eye(c, 45, 28, 5)
    eye(c, 55, 28, 5)
    // feathery antennae
    line(c, [
      [44, 20],
      [30, 8],
    ])
    line(c, [
      [56, 20],
      [70, 8],
    ])
    line(
      c,
      [
        [36, 14],
        [30, 16],
      ],
      3,
    )
    line(
      c,
      [
        [64, 14],
        [70, 16],
      ],
      3,
    )
  })

export const beetle: Painter = (c, size) =>
  inBox(c, size, () => {
    /**
     * MANDIBLES, NOT EARS. The first version drew two straight-sided triangles
     * off the head and the captured book page came back with something that
     * read as a cat: in a game whose whole point is telling one creature from
     * another, a beetle that looks like a cat is a content bug. They are now
     * thin curved pincers that hook back INWARD, which is the shape nothing
     * else in the book has.
     */
    for (const s of [-1, 1]) {
      c.beginPath()
      c.moveTo(50 + s * 9, 26)
      c.quadraticCurveTo(50 + s * 22, 16, 50 + s * 19, 3)
      c.lineWidth = 6
      c.strokeStyle = INK
      c.stroke()
      c.beginPath()
      c.moveTo(50 + s * 19, 3)
      c.quadraticCurveTo(50 + s * 13, 8, 50 + s * 6, 9)
      c.lineWidth = 5
      c.stroke()
    }
    ellipse(c, 50, 32, 13, 11)
    fillStroke(c, 'trunkDark')
    ellipse(c, 50, 62, 30, 30)
    fillStroke(c, 'trunk')
    line(
      c,
      [
        [50, 36],
        [50, 90],
      ],
      5,
    )
    dot(c, 34, 56, 5, 'woodDark')
    dot(c, 66, 56, 5, 'woodDark')
    eye(c, 44, 31, 4.5)
    eye(c, 56, 31, 4.5)
    line(
      c,
      [
        [22, 52],
        [8, 44],
      ],
      5,
    )
    line(
      c,
      [
        [78, 52],
        [92, 44],
      ],
      5,
    )
  })

export const snail: Painter = (c, size) =>
  inBox(c, size, () => {
    // body
    c.beginPath()
    c.moveTo(10, 82)
    c.quadraticCurveTo(14, 58, 34, 56)
    c.lineTo(74, 56)
    c.quadraticCurveTo(90, 60, 88, 82)
    c.closePath()
    fillStroke(c, 'monsterCream')
    // shell spiral
    ellipse(c, 56, 46, 30, 29)
    fillStroke(c, 'roofMustard')
    c.beginPath()
    c.arc(56, 46, 20, 0, Math.PI * 1.7)
    c.lineWidth = 5
    c.stroke()
    c.beginPath()
    c.arc(58, 48, 10, 0, Math.PI * 1.6)
    c.stroke()
    line(c, [
      [16, 62],
      [8, 44],
    ])
    line(c, [
      [24, 60],
      [22, 40],
    ])
    dot(c, 7, 42, 5, 'monsterCream')
    dot(c, 22, 38, 5, 'monsterCream')
    eye(c, 18, 70, 6)
  })

export const cicada: Painter = (c, size) =>
  inBox(c, size, () => {
    ellipse(c, 32, 58, 26, 15, -0.35)
    fillStroke(c, 'glassDark', 4)
    ellipse(c, 68, 58, 26, 15, 0.35)
    fillStroke(c, 'glassDark', 4)
    ellipse(c, 50, 54, 15, 34)
    fillStroke(c, 'grassDark')
    ellipse(c, 50, 26, 18, 15)
    fillStroke(c, 'leafDark')
    eye(c, 41, 24, 6)
    eye(c, 59, 24, 6)
    line(
      c,
      [
        [42, 66],
        [46, 84],
      ],
      4,
    )
    line(
      c,
      [
        [58, 66],
        [54, 84],
      ],
      4,
    )
  })

// ---------------------------------------------------------------------------
// fish
// ---------------------------------------------------------------------------

interface FishOpts {
  body: PaletteName
  fin: PaletteName
  /** Extra markings, drawn clipped to the body. */
  marks?: 'stripes' | 'spots' | 'rainbow' | 'none'
  whiskers?: boolean
  /** A star or a moon on the flank — how a kid tells the night fish apart. */
  badge?: 'moon' | 'star' | 'none'
}

/**
 * ===========================================================================
 *  THE BODY IS ONE SMOOTH, SYMMETRIC CURVE. READ THIS BEFORE MOVING A POINT.
 * ===========================================================================
 * Round `vr2`: "the fish art is visibly weaker than the bug art — a lumpy
 * uneven outline and an eye sitting half over the ink line, next to crisply
 * drawn sun and moon chips."
 *
 * Both were real and both were geometry. The old body was four QUADRATICS whose
 * joins were not tangent-continuous — the top run ended at (62,22) and the next
 * one left toward (88,32) on a different heading, so the back had a visible
 * kink in it and the nose came to a hard point; that is the lump. And the eye
 * sat at (24,40) where the top edge passes through y≈32 with a 6-unit ink
 * stroke on it, i.e. the eye's own circle and the outline overlapped by about
 * two units.
 *
 * It is now four CUBICS with mirrored control points, and the joins at (56,20),
 * (88,50) and (56,80) are collinear by construction, so the outline is smooth
 * all the way round and symmetric about y = 50 by inspection. The eye moved
 * back and down to (30,42), which is six units clear of the ink at that x, and
 * the smile came in with it. If you move the body, re-check both.
 */
function fishBody(c: CanvasRenderingContext2D): void {
  c.beginPath()
  c.moveTo(10, 50)
  c.bezierCurveTo(12, 28, 34, 18, 56, 20)
  c.bezierCurveTo(78, 22, 88, 34, 88, 50)
  c.bezierCurveTo(88, 66, 78, 78, 56, 80)
  c.bezierCurveTo(34, 82, 12, 72, 10, 50)
  c.closePath()
}

export function fish(o: FishOpts): Painter {
  return (c, size) =>
    inBox(c, size, () => {
      // tail
      c.beginPath()
      c.moveTo(84, 50)
      c.lineTo(97, 28)
      c.lineTo(94, 50)
      c.lineTo(97, 74)
      c.closePath()
      fillStroke(c, o.fin, 5)
      /**
       * TOP AND BOTTOM FINS, rooted INSIDE the body and standing well proud of
       * it. The control point is a long way outside the canvas ON PURPOSE: a
       * quadratic only reaches HALF way to its control, so `quadraticCurveTo(56,
       * 0, …)` from y 28 to y 30 peaks at y 14 — six units above a body whose
       * back is at y 20 and whose ink line is six wide, i.e. a dorsal fin
       * completely swallowed by its own outline. Read `19-book-one-card.png`
       * before moving either number.
       */
      c.beginPath()
      c.moveTo(40, 30)
      c.quadraticCurveTo(56, -24, 72, 32)
      c.closePath()
      fillStroke(c, o.fin, 5)
      c.beginPath()
      c.moveTo(40, 70)
      c.quadraticCurveTo(52, 118, 68, 70)
      c.closePath()
      fillStroke(c, o.fin, 5)
      // body
      fishBody(c)
      c.fillStyle = css(o.body)
      c.fill()
      // markings live inside the body outline
      if (o.marks !== undefined && o.marks !== 'none') {
        c.save()
        c.clip()
        if (o.marks === 'stripes') {
          c.fillStyle = css('flowerWhite')
          for (let i = 0; i < 3; i++) c.fillRect(30 + i * 18, 8, 8, 84)
        } else if (o.marks === 'spots') {
          c.fillStyle = css('flowerWhite')
          for (const [x, y, r] of [
            [34, 40, 7],
            [52, 60, 6],
            [66, 38, 5],
          ] as const) {
            c.beginPath()
            c.arc(x, y, r, 0, Math.PI * 2)
            c.fill()
          }
        } else {
          const bands: PaletteName[] = [
            'flowerRed',
            'roofMustard',
            'monsterLime',
            'uiAccent',
            'monsterLilac',
          ]
          bands.forEach((b, i) => {
            c.fillStyle = css(b)
            c.fillRect(14 + i * 15, 8, 15, 84)
          })
        }
        c.restore()
      }
      fishBody(c)
      c.lineWidth = 6
      c.strokeStyle = INK
      c.stroke()
      // side fin
      c.beginPath()
      c.moveTo(44, 54)
      c.quadraticCurveTo(56, 66, 40, 68)
      c.closePath()
      fillStroke(c, o.fin, 4)
      if (o.badge === 'moon') {
        c.beginPath()
        c.arc(62, 46, 13, 0, Math.PI * 2)
        c.fillStyle = css('flowerWhite')
        c.fill()
        c.beginPath()
        c.arc(68, 42, 12, 0, Math.PI * 2)
        c.fillStyle = css(o.body)
        c.fill()
      } else if (o.badge === 'star') {
        c.beginPath()
        for (let i = 0; i < 10; i++) {
          const r = i % 2 === 0 ? 14 : 6
          const ang = -Math.PI / 2 + (i * Math.PI) / 5
          const x = 60 + Math.cos(ang) * r
          const y = 48 + Math.sin(ang) * r
          if (i === 0) c.moveTo(x, y)
          else c.lineTo(x, y)
        }
        c.closePath()
        c.fillStyle = css('flowerYellow')
        c.fill()
        c.lineWidth = 3
        c.stroke()
      }
      if (o.whiskers === true) {
        line(
          c,
          [
            [18, 58],
            [6, 74],
          ],
          4,
        )
        line(
          c,
          [
            [20, 62],
            [14, 82],
          ],
          4,
        )
      }
      // a smile, because nothing in this game is grim. Kept low and forward of
      // the eye — overlapping them read as a second mouth on the cheek — and
      // INSIDE the outline: the snout is only about twenty units deep at x = 20,
      // so a wider arc here hangs out of the fish's chin.
      c.beginPath()
      c.arc(28, 58, 9, 0.15, Math.PI - 0.35)
      c.lineWidth = 4
      c.strokeStyle = INK
      c.stroke()
      eye(c, 30, 42, 7)
    })
}

export const tadpole: Painter = (c, size) =>
  inBox(c, size, () => {
    c.beginPath()
    c.moveTo(58, 50)
    c.quadraticCurveTo(76, 26, 92, 34)
    c.quadraticCurveTo(78, 50, 92, 70)
    c.quadraticCurveTo(76, 76, 58, 54)
    c.closePath()
    fillStroke(c, 'grassDark', 5)
    ellipse(c, 38, 50, 30, 26)
    fillStroke(c, 'leafDark')
    eye(c, 28, 42, 8)
    c.beginPath()
    c.arc(30, 56, 10, 0.15, Math.PI - 0.3)
    c.lineWidth = 4
    c.strokeStyle = INK
    c.stroke()
  })
